import React, { useState, useEffect } from 'react';

/* ============================================================
   LogicMojo Early 15 Offer — 15% OFF for the first 15 people UI
   Four pieces, all scoped under .lm-anniv* class names:
     1. <AnniversaryTopBar />     — in-flow strip above the hero; scrolls away (desktop / tablet)
     2. <AnniversaryHeroBadge />  — premium badge inside the dark hero
     3. <AnniversaryPricingCard /> — highlighted card in the fee section
     4. <AnniversaryMobileCTA />  — dismissible bottom bar (mobile only)
   Palette: Indian tricolour — saffron #ff9933, white, green #138808 —
   on deep navy #071a3d, with a subtle Ashoka-chakra motif.
   ============================================================ */

const CALENDLY_URL =
  'https://calendly.com/logicmojo/schedule-call-back-from-experts-for-live-classes?month=2026-06';

const openCalendly = (e) => {
  e.preventDefault();
  window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
};

/* Small Ashoka-chakra wheel (24 spokes), inherits currentColor */
const Chakra = ({ className }) => (
  <svg className={className} viewBox="0 0 100 100" fill="none" stroke="currentColor" aria-hidden="true">
    <circle cx="50" cy="50" r="44" strokeWidth="6" />
    <circle cx="50" cy="50" r="8" strokeWidth="6" />
    {Array.from({ length: 24 }, (_, i) => {
      const a = (i * 15 * Math.PI) / 180;
      return (
        <line
          key={i}
          x1={50 + 12 * Math.cos(a)}
          y1={50 + 12 * Math.sin(a)}
          x2={50 + 40 * Math.cos(a)}
          y2={50 + 40 * Math.sin(a)}
          strokeWidth="4"
        />
      );
    })}
  </svg>
);

/* ---------- 1. Top announcement bar (in flow, scrolls with the page) ---------- */

export const AnniversaryTopBar = () => (
  <div className="lm-anniv-topbar" role="region" aria-label="LogicMojo Early 15 Offer">
    <div className="lm-anniv-topbar__inner">
      <p className="lm-anniv-topbar__text">
        <b>Early 15 Offer:</b>&nbsp;Get 15% off on the AI &amp; ML
        Course — only for the first 15 people.
      </p>
      <a href={CALENDLY_URL} onClick={openCalendly} className="lm-anniv-topbar__cta">
        Claim 15% OFF
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
      </a>
    </div>
    <style>{`
      .lm-anniv-topbar{position:relative;z-index:1300;width:100%;background:linear-gradient(100deg,#071a3d 0%,#0b2a5c 48%,#0d3470 100%);box-shadow:0 10px 24px -14px rgba(7,26,61,.8);font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;}
      .lm-anniv-topbar::before{content:"";position:absolute;left:0;right:0;top:0;height:3px;background:linear-gradient(90deg,#ff9933 0%,#ff9933 33.33%,#fff 33.33%,#fff 66.66%,#138808 66.66%,#138808 100%);}
      .lm-anniv-topbar::after{content:"";position:absolute;left:0;right:0;bottom:0;height:2px;background:linear-gradient(90deg,transparent,#ff9933 30%,#e2e8f0 50%,#2eab4f 70%,transparent);opacity:.85;}
      .lm-anniv-topbar__inner{max-width:1200px;height:46px;margin:0 auto;display:flex;flex-wrap:nowrap;align-items:center;justify-content:center;gap:14px;padding:0 18px;}
      .lm-anniv-topbar__text{margin:0;font-size:13.5px;line-height:1.45;font-weight:500;color:#dbe7ff;letter-spacing:.01em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
      .lm-anniv-topbar__text b{font-weight:800;background:linear-gradient(90deg,#ffb95e,#ffe9d1 55%,#7ee2a0);-webkit-background-clip:text;background-clip:text;color:transparent;}
      .lm-anniv-topbar__cta{flex-shrink:0;display:inline-flex;align-items:center;gap:7px;font-size:12.5px;font-weight:800;letter-spacing:.04em;color:#4a2400;text-decoration:none;padding:7px 16px;border-radius:999px;background:linear-gradient(100deg,#ffb95e,#ff9933 60%,#f28518);box-shadow:0 6px 16px -6px rgba(255,153,51,.7),inset 0 0 0 1px rgba(255,255,255,.45);transition:transform .25s ease,box-shadow .25s ease;}
      .lm-anniv-topbar__cta:hover{transform:translateY(-1px);color:#4a2400;text-decoration:none;box-shadow:0 10px 22px -8px rgba(255,153,51,.9),inset 0 0 0 1px rgba(255,255,255,.45);}
      .lm-anniv-topbar__cta svg{width:13px;height:13px;}
      @media(max-width:991px){.lm-anniv-topbar__text{font-size:12.5px;}}
      @media(max-width:767px){.lm-anniv-topbar{display:none;}}
    `}</style>
  </div>
);

/* ---------- 2. Hero offer badge (sits on the dark hero) ---------- */

export const AnniversaryHeroBadge = () => (
  <div className="lm-anniv-hero">
    <div className="lm-anniv-hero__pill">
      <Chakra className="lm-anniv-hero__chakra" />
      Early 15 — 15% Discount Live
    </div>
    <p className="lm-anniv-hero__sub">Special pricing — only for the first 15 people.</p>
    <style>{`
      .lm-anniv-hero{margin:0 0 1.4rem;font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;}
      .lm-anniv-hero__pill{position:relative;display:inline-flex;align-items:center;gap:9px;font-size:12.5px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:#fff;padding:8px 18px;border-radius:999px;background:linear-gradient(100deg,rgba(255,153,51,.28),rgba(11,42,92,.4) 50%,rgba(19,136,8,.3));border:1px solid rgba(255,213,166,.45);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);box-shadow:0 8px 22px -10px rgba(255,153,51,.55),inset 0 0 18px rgba(255,213,166,.12);}
      .lm-anniv-hero__chakra{width:15px;height:15px;color:#9ec5ff;animation:lmAnnivSpin 14s linear infinite;}
      .lm-anniv-hero__sub{margin:8px 0 0 4px;font-size:13.5px;font-weight:500;color:rgba(224,231,255,.8);letter-spacing:.01em;}
      @keyframes lmAnnivSpin{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
      @media(prefers-reduced-motion:reduce){.lm-anniv-hero__chakra{animation:none;}}
      @media(max-width:767px){.lm-anniv-hero__pill{font-size:11.5px;padding:7px 14px;}.lm-anniv-hero__sub{font-size:12.5px;}}
    `}</style>
  </div>
);

/* ---------- 3. Pricing-section discount card ---------- */

export const AnniversaryPricingCard = () => (
  <div className="lm-anniv-fee">
    <Chakra className="lm-anniv-fee__wheel" />
    <div className="lm-anniv-fee__body">
      <span className="lm-anniv-fee__tag">
        Early 15 Special
      </span>
      <h3 className="lm-anniv-fee__title">
        Early 15 — <span className="lm-anniv-fee__pct">15% OFF</span>
      </h3>
      <p className="lm-anniv-fee__text">
        Enroll early and get a flat 15% discount on enrollment.
        Limited offer — only for the first 15 people.
      </p>
    </div>
    <a href={CALENDLY_URL} onClick={openCalendly} className="lm-anniv-fee__cta">
      Book a Free Call to Claim 15% OFF
      <i className="fa fa-angle-right" aria-hidden="true"></i>
    </a>
    <style>{`
      .lm-anniv-fee{position:relative;overflow:hidden;display:flex;align-items:center;gap:26px;margin:0 0 26px;padding:28px 30px;border-radius:22px;background:linear-gradient(120deg,#fff7ec 0%,#ffffff 50%,#eefaf0 100%);border:1px solid #ffe0b8;box-shadow:0 22px 44px -26px rgba(255,132,24,.45);font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;}
      .lm-anniv-fee::before{content:"";position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,#ff9933 0%,#ff9933 33.33%,#e2e8f0 33.33%,#e2e8f0 66.66%,#138808 66.66%,#138808 100%);}
      .lm-anniv-fee__wheel{position:absolute;width:190px;height:190px;top:-58px;right:-48px;color:#1e3a8a;opacity:.08;pointer-events:none;animation:lmAnnivWheel 26s linear infinite;}
      @keyframes lmAnnivWheel{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
      @media(prefers-reduced-motion:reduce){.lm-anniv-fee__wheel{animation:none;}}
      .lm-anniv-fee__body{flex:1;min-width:0;position:relative;}
      .lm-anniv-fee__tag{display:inline-flex;align-items:center;gap:7px;font-size:11.5px;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:#c05600;background:#fff;border:1px solid #ffd9a8;padding:6px 14px;border-radius:999px;margin-bottom:12px;box-shadow:0 6px 16px -8px rgba(255,153,51,.55);}
      .lm-anniv-fee__title{font-size:22px;font-weight:800;color:#0f172a;margin:0 0 7px;letter-spacing:-.01em;}
      .lm-anniv-fee__pct{background:linear-gradient(100deg,#e26a00,#1e3a8a 55%,#0f7a1f);-webkit-background-clip:text;background-clip:text;color:transparent;}
      .lm-anniv-fee__text{font-size:14.5px;line-height:1.6;color:#5b6577;margin:0;max-width:560px;}
      .lm-anniv-fee__cta{position:relative;overflow:hidden;flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;gap:9px;text-decoration:none;font-size:15px;font-weight:700;padding:15px 26px;border-radius:13px;color:#fff;background:linear-gradient(100deg,#ff8a1e,#e26a00 45%,#0f7a1f);box-shadow:0 16px 30px -12px rgba(226,106,0,.75);transition:transform .25s ease,box-shadow .25s ease;white-space:nowrap;}
      .lm-anniv-fee__cta:hover{transform:translateY(-2px);color:#fff;text-decoration:none;box-shadow:0 22px 40px -10px rgba(226,106,0,.95);}
      .lm-anniv-fee__cta::after{content:"";position:absolute;top:0;left:-130%;width:60%;height:100%;background:linear-gradient(100deg,transparent,rgba(255,255,255,.45),transparent);transform:skewX(-20deg);transition:left .6s ease;}
      .lm-anniv-fee__cta:hover::after{left:140%;}
      @media(max-width:860px){.lm-anniv-fee{flex-direction:column;align-items:flex-start;gap:18px;padding:24px 22px;}.lm-anniv-fee__cta{width:100%;white-space:normal;text-align:center;}}
    `}</style>
  </div>
);

/* ---------- 4. Mobile-only sticky bottom CTA ---------- */

export const AnniversaryMobileCTA = () => {
  const [dismissed, setDismissed] = useState(true);
  const [pastHero, setPastHero] = useState(false);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem('lmAnnivMobileCtaClosed') === '1');
    } catch (err) {
      setDismissed(false);
    }
  }, []);

  // Only show once the user has scrolled past the hero banner (.mib_container)
  useEffect(() => {
    const hero = document.querySelector('.mib_container');

    if (hero && typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver(
        ([entry]) => {
          // Past the hero = hero no longer intersects and sits above the viewport
          setPastHero(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
        },
        { threshold: 0 }
      );
      observer.observe(hero);
      return () => observer.disconnect();
    }

    // Fallback: window-scroll check against hero height (or one viewport)
    const check = () => {
      const el = document.querySelector('.mib_container');
      const threshold = el ? el.offsetTop + el.offsetHeight : window.innerHeight;
      setPastHero((window.pageYOffset || document.documentElement.scrollTop) > threshold - 60);
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, []);

  const close = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem('lmAnnivMobileCtaClosed', '1');
    } catch (err) {
      /* private mode — dismiss for this render only */
    }
  };

  if (dismissed || !pastHero) return null;

  return (
    <div className="lm-anniv-mcta" role="region" aria-label="Early 15 15% discount">
      <span className="lm-anniv-mcta__text">Early 15 — 15% Discount for First 15 People</span>
      <button type="button" className="lm-anniv-mcta__close" onClick={close} aria-label="Close offer bar">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>
      <style>{`
        .lm-anniv-mcta{display:none;font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;}
        @media(max-width:767px){
          /* Sits just above the fixed "Chat with us" / "Request a Call"
             pills (bottom:16px, ~44px tall) so neither covers the other. */
          .lm-anniv-mcta{position:fixed;left:10px;right:10px;bottom:calc(72px + env(safe-area-inset-bottom,0px));z-index:1180;display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:16px;background:linear-gradient(100deg,rgba(7,26,61,.97),rgba(13,52,112,.97));border:1px solid rgba(255,185,94,.45);box-shadow:0 14px 34px -12px rgba(7,26,61,.85);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);}
          .lm-anniv-mcta::before{content:"";position:absolute;left:14px;right:14px;top:0;height:2.5px;border-radius:2px;background:linear-gradient(90deg,#ff9933 0%,#ff9933 33.33%,#fff 33.33%,#fff 66.66%,#138808 66.66%,#138808 100%);}
          .lm-anniv-mcta__text{flex:1;min-width:0;font-size:13.5px;font-weight:700;color:#e8efff;letter-spacing:.01em;white-space:normal;overflow:visible;}
          .lm-anniv-mcta__close{flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;padding:0;border:none;border-radius:50%;background:rgba(255,255,255,.12);color:#ffd9a8;cursor:pointer;}
          .lm-anniv-mcta__close svg{width:12px;height:12px;}
        }
      `}</style>
    </div>
  );
};
